import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import {
  catalogQuery,
  saveCatalogEntry,
  setCatalogActive,
  type CatalogEntry,
  type CatalogPage,
  type CatalogRule,
} from '../../api/businessAppsDetect'
import { Modal } from '../../ui/Modal'
import { toast } from '../../ui/notify'
import { failed } from '../parts'

/**
 * Catalogul dupa care cauta "Find applications": al platformei (Charisma,
 * Opera, MICROS, Gantner, SAGA...) si al furnizorului. O intrare a platformei
 * se poate stinge sau "Customize" (o copie a furnizorului, cu acelasi cod, o
 * inlocuieste); una proprie se scrie de la zero.
 */
export function CatalogModal({ onClose }: { onClose(): void }) {
  const page = useQuery(catalogQuery())
  const [editing, setEditing] = useState<CatalogEntry | 'new' | null>(null)

  return (
    <Modal id="badCatalogModal" open large onClose={onClose} title="Application catalog" footer={
      <button type="button" className="btn btn-default" onClick={onClose}>
        Close
      </button>
    }>
      {!page.data ? (
        <p className="text-muted">Loading...</p>
      ) : editing !== null ? (
        <EntryForm page={page.data} entry={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />
      ) : (
        <CatalogList page={page.data} onEdit={setEditing} />
      )}
    </Modal>
  )
}

function CatalogList({ page, onEdit }: { page: CatalogPage; onEdit(entry: CatalogEntry | 'new'): void }) {
  const query = useQueryClient()
  const kinds = Object.fromEntries(page.kinds.map((k) => [k.value, k.label]))

  const toggle = (entry: CatalogEntry) => {
    setCatalogActive(entry.code, !entry.active)
      .then((result) => {
        toast(result.message, 'Applications', result.status === 'OK' ? 'success' : 'error')
        void query.invalidateQueries({ queryKey: ['business-apps', 'catalog'] })
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
  }

  return (
    <>
      <p className="text-muted small">
        What "Find applications" looks for on the machines. Patterns ignore case; <code>*</code> stands for anything.
      </p>
      {page.canEdit && page.ready && (
        <p>
          <button type="button" className="btn btn-success btn-sm" id="badCatalogAdd" onClick={() => onEdit('new')}>
            <i className="fas fa-plus"></i> Add your own
          </button>
        </p>
      )}
      <table className="table table-condensed bad-catalog-table" id="badCatalogTable">
        <thead>
          <tr>
            <th>Application</th>
            <th>Looks for</th>
            <th>Source</th>
            <th>Active</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {page.entries.map((e) => (
            <tr key={e.code} className={e.active ? undefined : 'text-muted'}>
              <td>
                <strong>{e.name}</strong>
                {e.vendor !== '' && <div className="adm-muted small">{e.vendor}</div>}
              </td>
              <td>
                {Array.from(new Set(e.rules.map((r) => kinds[r.kind] ?? r.kind))).join(', ')}
                <div className="adm-muted small">{e.rules.length} rules</div>
              </td>
              <td>{e.own ? (e.overridden ? 'Customized' : 'Yours') : 'Platform'}</td>
              <td>
                {page.canEdit && page.ready ? (
                  <input type="checkbox" aria-label={`Look for ${e.name}`} checked={e.active} onChange={() => toggle(e)} />
                ) : e.active ? (
                  'Yes'
                ) : (
                  'No'
                )}
              </td>
              <td>
                {page.canEdit && page.ready && (
                  <button type="button" className="btn btn-link btn-xs" onClick={() => onEdit(e)}>
                    {e.own ? 'Edit' : 'Customize'}
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  )
}

const EMPTY_RULE: CatalogRule = { kind: 'software', pattern: '', role: 2, weight: 2, colocated: false }

function EntryForm({ page, entry, onDone }: { page: CatalogPage; entry: CatalogEntry | null; onDone(): void }) {
  const query = useQueryClient()
  const [name, setName] = useState(entry?.name ?? '')
  const [vendor, setVendor] = useState(entry?.vendor ?? '')
  const [description, setDescription] = useState(entry?.description ?? '')
  const [criticality, setCriticality] = useState(entry?.criticality ?? 3)
  const [match, setMatch] = useState((entry?.match ?? []).join('\n'))
  const [rules, setRules] = useState<CatalogRule[]>(entry?.rules.length ? entry.rules : [{ ...EMPTY_RULE }])
  const [contact, setContact] = useState(entry?.contact ?? { name: '', email: '', phone: '', url: '' })
  const [busy, setBusy] = useState(false)

  const setRule = (i: number, change: Partial<CatalogRule>) => setRules(rules.map((r, j) => (j === i ? { ...r, ...change } : r)))

  const save = () => {
    setBusy(true)
    saveCatalogEntry({
      code: entry?.code ?? '',
      name,
      vendor,
      description,
      criticality,
      match,
      rules,
      contact_name: contact.name,
      contact_email: contact.email,
      contact_phone: contact.phone,
      contact_url: contact.url,
    })
      .then((result) => {
        if (result.status !== 'OK') {
          toast(result.message, 'Applications', 'error')
          return
        }
        toast(result.message, 'Applications')
        void query.invalidateQueries({ queryKey: ['business-apps', 'catalog'] })
        onDone()
      })
      .catch((error) => toast(failed(error), 'Applications', 'error'))
      .finally(() => setBusy(false))
  }

  return (
    <div id="badEntryForm">
      {entry !== null && !entry.own && (
        <p className="alert alert-info small">Your copy replaces the platform entry for your customers. Switch it off to go back.</p>
      )}
      <div className="row">
        <div className="col-sm-6 form-group">
          <label htmlFor="badName">Name</label>
          <input id="badName" className="form-control" maxLength={190} value={name} onChange={(e) => setName(e.target.value)} />
        </div>
        <div className="col-sm-4 form-group">
          <label htmlFor="badVendor">Vendor</label>
          <input id="badVendor" className="form-control" maxLength={190} value={vendor} onChange={(e) => setVendor(e.target.value)} />
        </div>
        <div className="col-sm-2 form-group">
          <label htmlFor="badCriticality">Criticality</label>
          <select id="badCriticality" className="form-control" value={criticality} onChange={(e) => setCriticality(Number(e.target.value))}>
            {page.criticality.map((c) => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div className="form-group">
        <label htmlFor="badDescription">Description</label>
        <input id="badDescription" className="form-control" maxLength={500} value={description} onChange={(e) => setDescription(e.target.value)} />
      </div>
      <label>Look for</label>
      {rules.map((r, i) => (
        <div className="bad-rule-row" key={i}>
          <select className="form-control input-sm" aria-label="Kind" value={r.kind} onChange={(e) => setRule(i, { kind: e.target.value })}>
            {page.kinds.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
          <input
            className="form-control input-sm bad-pattern"
            aria-label="Pattern"
            maxLength={190}
            placeholder="e.g. Charisma*"
            value={r.pattern}
            onChange={(e) => setRule(i, { pattern: e.target.value })}
          />
          <select className="form-control input-sm" aria-label="Role" value={r.role} onChange={(e) => setRule(i, { role: Number(e.target.value) })}>
            {page.roles.map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
          <select className="form-control input-sm" aria-label="Weight" value={r.weight} onChange={(e) => setRule(i, { weight: Number(e.target.value) })}>
            <option value={3}>sure</option>
            <option value={2}>likely</option>
            <option value={1}>hint</option>
          </select>
          <label title="Counts only on a machine that already looks like a server of this application">
            <input type="checkbox" checked={r.colocated} onChange={(e) => setRule(i, { colocated: e.target.checked })} /> only with a server match
          </label>
          <button type="button" className="btn btn-link btn-xs" aria-label="Remove rule" onClick={() => setRules(rules.filter((_, j) => j !== i))}>
            <i className="fas fa-times"></i>
          </button>
        </div>
      ))}
      <p>
        <button type="button" className="btn btn-default btn-xs" onClick={() => setRules([...rules, { ...EMPTY_RULE }])}>
          <i className="fas fa-plus"></i> Add rule
        </button>
      </p>
      <div className="form-group">
        <label htmlFor="badMatch">Already recorded under these names (one per line)</label>
        <textarea id="badMatch" className="form-control" rows={2} value={match} onChange={(e) => setMatch(e.target.value)} />
      </div>
      <label>Vendor support (put on the application when it is created)</label>
      <div className="row">
        <div className="col-sm-3 form-group">
          <input className="form-control input-sm" aria-label="Support contact name" placeholder="Name" value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })} />
        </div>
        <div className="col-sm-3 form-group">
          <input className="form-control input-sm" aria-label="Support email" placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
        </div>
        <div className="col-sm-3 form-group">
          <input className="form-control input-sm" aria-label="Support phone" placeholder="Phone" value={contact.phone} onChange={(e) => setContact({ ...contact, phone: e.target.value })} />
        </div>
        <div className="col-sm-3 form-group">
          <input className="form-control input-sm" aria-label="Support portal" placeholder="https://" value={contact.url} onChange={(e) => setContact({ ...contact, url: e.target.value })} />
        </div>
      </div>
      <p>
        <button type="button" className="btn btn-default" onClick={onDone}>
          Back
        </button>{' '}
        <button type="button" className="btn btn-primary" id="badEntrySave" disabled={busy} onClick={save}>
          Save
        </button>
      </p>
    </div>
  )
}
